import React from 'react';
import { call, cancel, delay, fork, put, takeEvery } from 'redux-saga/effects';
import {
  postPlatformComponentRestart,
  fetchPlatformComponentStatus,
  restartResources,
  fetchNotificationLabels,
} from '@kineticdata/react';
import { Icon, Alert, Tooltip } from '@kineticdata/ui';
import { action, dispatch, regHandlers, regRoute, regSaga } from '../../store';
import { useSelector } from 'react-redux';
import { List, fromJS } from 'immutable';
import classNames from 'classnames';

regHandlers({
  SET_NOTIFICATION_LABELS: (state, { payload }) =>
    state.setIn(['page', 'notificationLabels'], payload),
  SYSTEM_COMPONENT_RESTART_MESSAGE: (state, { payload }) =>
    state.setIn(['page', 'system', 'components', 'message'], payload),
  CLEAR_SYSTEM_COMPONENT_RESTART_MESSAGE: state =>
    state.deleteIn(['page', 'system', 'components', 'message']),
  SET_SYSTEM_STATUS_COMPONENTS: (state, { payload }) =>
    state.setIn(['page', 'system', 'components', 'components'], payload),
});

regSaga(
  takeEvery('RESTART_RESOURCES', function*({ payload }) {
    const { namespace, label } = payload;
    yield call(restartResources, {
      notification: { namespace, label },
    });
  }),
);

regSaga(
  takeEvery('SYSTEM_COMPONENT_RESTART', function*({ payload }) {
    try {
      const result = yield call(postPlatformComponentRestart, {
        component: payload,
      });

      if (result.error) {
        yield put({
          type: 'SYSTEM_COMPONENT_RESTART_MESSAGE',
          payload: {
            error: true,
            message: `There was a problem restarting "${payload}": ${
              result.error.message
            }`,
          },
        });
      } else {
        yield put({
          type: 'SYSTEM_COMPONENT_RESTART_MESSAGE',
          payload: {
            error: false,
            message: `Restarting "${payload} has been requested successfully.`,
          },
        });
      }
    } catch (e) {
      console.warn('There was a problem restarting the platform component.', e);
    }
  }),
);

let pollerTask;
function* pollStatusTask() {
  while (true) {
    const result = yield call(fetchPlatformComponentStatus);
    yield put(action('SET_SYSTEM_STATUS_COMPONENTS', List(result.components)));
    yield delay(3000);
  }
}

regRoute('SYSTEM_COMPONENTS', '/system/components', {
  enterTask: function*() {
    pollerTask = yield fork(pollStatusTask);
  },
  leaveTask: function*() {
    yield cancel(pollerTask);
  },
});

regSaga(
  takeEvery('ENTER_SYSTEM_COMPONENTS', function*() {
    const results = yield call(fetchNotificationLabels);
    yield put(action('SET_NOTIFICATION_LABELS', fromJS(results.notifications)));
  }),
);

const RESTARTABLE_COMPONENTS = [
  { name: 'agent', label: 'Agent' },
  { name: 'core', label: 'Core' },
  { name: 'loghub', label: 'Loghub' },
  { name: 'indexer', label: 'Indexer' },
];

const restartComponent = componentName => () =>
  dispatch('SYSTEM_COMPONENT_RESTART', componentName);
const handleRestart = notification => () =>
  dispatch(action('RESTART_RESOURCES', notification));

export const SystemComponents = () => {
  const {
    statusMessage,
    components,
    notifications,
    notificationLabels,
  } = useSelector(state => ({
    statusMessage: state.getIn(['page', 'system', 'components', 'message']),
    components: state.getIn(
      ['page', 'system', 'components', 'components'],
      List(),
    ),
    notificationLabels: state.getIn(['page', 'notificationLabels'], List()),
    notifications: state.getIn(['app', 'notifications', 'data'], List()),
  }));

  const restartButtons = notificationLabels.map(label => {
    const notification = notifications.find(
      notification =>
        notification.get('label') === label.get('label') &&
        notification.get('namespace') === label.get('namespace'),
    );

    if (notification) {
      label = label.set('message', notification.get('message'));
    }

    return label.set('isRestartRequired', !!notification);
  });

  return (
    <div className="app-content__main">
      <div className="heading">
        <h1>System Servers</h1>
      </div>

      {statusMessage && (
        <Alert color={statusMessage.error ? 'danger' : 'info'}>
          {statusMessage.message}
        </Alert>
      )}

      <div className="row">
        <div className="col-lg-7 mb-4">
          <div className="content-container">
            <h2>System Components</h2>
            {RESTARTABLE_COMPONENTS.map((component, i) => {
              const status = components.getIn(
                [
                  components.findIndex(c => c.name === component.name),
                  'status',
                ],
                'Loading',
              );

              return (
                <div key={component.name}>
                  <div
                    className={classNames('heading heading-sm', {
                      'mt-4': i !== 0,
                    })}
                  >
                    <h2 className="h3">{component.label}</h2>
                  </div>
                  <div className="input-group input-group--map-field">
                    <div
                      className={classNames('form-control-plaintext rounded', {
                        'animate-success-bg': status === 'Running',
                        'bg-light': status === 'Loading',
                        'bg-danger':
                          status !== 'Running' && status !== 'Loading',
                      })}
                      style={{ maxWidth: '40vw' }}
                    />
                    <button
                      className="btn btn-outline-primary"
                      onClick={restartComponent(component.name)}
                      disabled={status !== 'Running'}
                    >
                      Restart
                    </button>
                  </div>
                  <div className="form-control-plaintext">
                    <em className="fw-semibold">{status}</em>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="col">
          <div className="content-container">
            <h2>Component Dependent Restarts</h2>
            {restartButtons &&
              restartButtons.map(label => {
                return (
                  <div
                    key={`${label.get('namespace')}-${label.get('label')}`}
                    className="d-flex align-items-center my-3"
                  >
                    <button
                      type="button"
                      className="btn btn-outline-primary justify-content-start w-75"
                      onClick={handleRestart(label.toJS())}
                      aria-label="Refresh"
                      title="Refresh"
                    >
                      <Icon name="refresh-cw" md />
                      <span>Restart {label.get('friendlyLabel')}</span>
                    </button>
                    {label.get('isRestartRequired') && (
                      <Tooltip content={label.get('message')}>
                        {({ ref }) => (
                          <span ref={ref} className="ms-2">
                            <Icon
                              name="alert-octagon"
                              color="#F36C23"
                              bg="#FDE5D8"
                            />
                          </span>
                        )}
                      </Tooltip>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
};
